import React, { useCallback, useMemo, useState } from 'react';

import { AtmOperationType, AtmTransaction } from '../../types/atm';

interface AtmTransactionHistoryProps {
  transactions: AtmTransaction[];
  atmName: string;
  className?: string;
  maxItems?: number;
}

interface FilterState {
  operationType: AtmOperationType | 'all';
  dateRange: 'today' | 'week' | 'month' | 'all';
  sortBy: 'date' | 'amount' | 'operation';
  sortDirection: 'asc' | 'desc';
}

const AtmTransactionHistory: React.FC<AtmTransactionHistoryProps> = ({
  transactions,
  atmName,
  className = '',
  maxItems = 50,
}) => {
  const [filters, setFilters] = useState<FilterState>({
    operationType: 'all',
    dateRange: 'all',
    sortBy: 'date',
    sortDirection: 'desc',
  });

  const [showFilters, setShowFilters] = useState(false);

  // Helper function to safely convert timestamps to Date objects
  const getDateFromTimestamp = useCallback((timestamp: any): Date => {
    if (timestamp instanceof Date) {
      return timestamp;
    }
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    if (typeof timestamp === 'string') {
      return new Date(timestamp);
    }
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    // Fallback to current date if timestamp is invalid
    return new Date();
  }, []);

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Filter by operation type
    if (filters.operationType !== 'all') {
      filtered = filtered.filter(tx => tx.type === filters.operationType);
    }

    // Filter by date range
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    switch (filters.dateRange) {
      case 'today':
        filtered = filtered.filter(tx => getDateFromTimestamp(tx.timestamp) >= today);
        break;
      case 'week':
        filtered = filtered.filter(tx => getDateFromTimestamp(tx.timestamp) >= weekAgo);
        break;
      case 'month':
        filtered = filtered.filter(tx => getDateFromTimestamp(tx.timestamp) >= monthAgo);
        break;
    }

    // Sort transactions
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (filters.sortBy) {
        case 'amount':
          comparison = (a.amount || 0) - (b.amount || 0);
          break;
        case 'operation':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'date':
        default: {
          const aTime = getDateFromTimestamp(a.timestamp).getTime();
          const bTime = getDateFromTimestamp(b.timestamp).getTime();
          comparison = aTime - bTime;
          break;
        }
      }

      return filters.sortDirection === 'desc' ? -comparison : comparison;
    });

    return filtered.slice(0, maxItems);
  }, [transactions, filters, maxItems, getDateFromTimestamp]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalAmount = transactions.reduce((sum, tx) => sum + (tx.amount || 0), 0);
    const totalTransactions = transactions.length;
    const operationCounts = transactions.reduce(
      (counts, tx) => {
        counts[tx.type] = (counts[tx.type] || 0) + 1;
        return counts;
      },
      {} as Record<string, number>
    );

    return {
      totalAmount,
      totalTransactions,
      operationCounts,
      averageAmount: totalTransactions > 0 ? totalAmount / totalTransactions : 0,
    };
  }, [transactions]);

  // Get unique operation types
  const uniqueOperations = useMemo(() => {
    return Array.from(new Set(transactions.map(tx => tx.type)));
  }, [transactions]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getOperationIcon = (operationType: AtmOperationType) => {
    switch (operationType) {
      case AtmOperationType.WITHDRAWAL:
        return '💳';
      case AtmOperationType.DEPOSIT:
        return '💰';
      case AtmOperationType.BALANCE:
        return '📊';
      case AtmOperationType.TRANSFER:
        return '🔄';
      default:
        return '💼';
    }
  };

  const getOperationColor = (operationType: AtmOperationType) => {
    switch (operationType) {
      case AtmOperationType.WITHDRAWAL:
        return 'text-red-600 bg-red-50';
      case AtmOperationType.DEPOSIT:
        return 'text-green-600 bg-green-50';
      case AtmOperationType.BALANCE:
        return 'text-blue-600 bg-blue-50';
      case AtmOperationType.TRANSFER:
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Transaction History</h3>
            <p className="text-sm text-gray-600">{atmName}</p>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {showFilters ? 'Hide' : 'Show'} Filters
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-900">{stats.totalTransactions}</div>
            <div className="text-sm text-gray-600">Total Transactions</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">${stats.totalAmount.toFixed(2)}</div>
            <div className="text-sm text-gray-600">Total Amount</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">
              ${stats.averageAmount.toFixed(2)}
            </div>
            <div className="text-sm text-gray-600">Average Amount</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600">
              {Object.keys(stats.operationCounts).length}
            </div>
            <div className="text-sm text-gray-600">Operation Types</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Operation Type Filter */}
            <div>
              <label
                htmlFor="operation-filter"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Operation Type
              </label>
              <select
                id="operation-filter"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.operationType}
                onChange={e =>
                  setFilters(prev => ({
                    ...prev,
                    operationType: e.target.value as AtmOperationType | 'all',
                  }))
                }
              >
                <option value="all">All Types</option>
                {uniqueOperations.map(op => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range Filter */}
            <div>
              <label htmlFor="date-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <select
                id="date-filter"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.dateRange}
                onChange={e =>
                  setFilters(prev => ({
                    ...prev,
                    dateRange: e.target.value as FilterState['dateRange'],
                  }))
                }
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label
                htmlFor="sort-by-filter"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Sort By
              </label>
              <select
                id="sort-by-filter"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.sortBy}
                onChange={e =>
                  setFilters(prev => ({
                    ...prev,
                    sortBy: e.target.value as FilterState['sortBy'],
                  }))
                }
              >
                <option value="date">Date</option>
                <option value="amount">Amount</option>
                <option value="operation">Operation</option>
              </select>
            </div>

            {/* Sort Direction */}
            <div>
              <label
                htmlFor="sort-order-filter"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Sort Order
              </label>
              <select
                id="sort-order-filter"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.sortDirection}
                onChange={e =>
                  setFilters(prev => ({
                    ...prev,
                    sortDirection: e.target.value as FilterState['sortDirection'],
                  }))
                }
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Transaction List */}
      <div className="p-4">
        {filteredTransactions.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-4xl mb-4">📋</div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">No transactions found</h4>
            <p className="text-gray-600">
              {transactions.length === 0
                ? 'No transactions have been made yet.'
                : 'No transactions match your current filters.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTransactions.map(transaction => (
              <div
                key={transaction.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm ${getOperationColor(
                      transaction.type
                    )}`}
                  >
                    {getOperationIcon(transaction.type)}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{transaction.type}</div>
                    <div className="text-sm text-gray-600">
                      {formatDate(getDateFromTimestamp(transaction.timestamp))} at{' '}
                      {formatTime(getDateFromTimestamp(transaction.timestamp))}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-gray-900">
                    ${(transaction.amount || 0).toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500">ID: {transaction.id.slice(-6)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Show More Button */}
      {transactions.length > maxItems && (
        <div className="p-4 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-600">
            Showing {Math.min(maxItems, filteredTransactions.length)} of {transactions.length}{' '}
            transactions
          </p>
        </div>
      )}
    </div>
  );
};

export default AtmTransactionHistory;
